import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// ✅ Clean imports
import { SERVER_URL, fetchRideById } from "../utils/api";
import "../css/bookedrides.css"; 

export default function BookedRideCard({ ride, request, user }) {
  const navigate = useNavigate();
  const [hasRated, setHasRated] = useState(false);
  const currentUserId = user?._id || user?.id;

  // Safety guard: if no ride data, don't render anything
  if (!ride) return null;

  const driver = ride.postedBy || ride.driver || {};
  
  const avatar = driver.avatar 
    ? (driver.avatar.startsWith("http") ? driver.avatar : `${SERVER_URL}${driver.avatar}`)
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(driver.name || 'U')}&background=0B74FF&color=fff`;

  // --- 1. STATUS LOGIC ---
  const req = request || {};
  let status = (req.status || 'pending').toLowerCase();
  
  if (ride.status === 'cancelled') {
    status = 'cancelled';
  } else if (ride.status === 'completed' || ride.completed) {
    status = 'completed';
  }

  // --- 2. SMART RATING CHECK ---
  useEffect(() => {
    let isMounted = true;
    const checkRatingStatus = async () => {
      if (status === 'completed' && ride._id && currentUserId) {
        try {
          const token = localStorage.getItem("travelo_token");
          const fullRide = await fetchRideById(ride._id, token);
          
          if (isMounted && fullRide?.ratings) {
            const found = fullRide.ratings.some((r) => {
              const raterId = r.user?._id || r.user || r.from?._id || r.from;
              return String(raterId) === String(currentUserId);
            });
            setHasRated(found);
          }
        } catch (err) {
          console.log("Rating check skipped or failed", err);
        }
      }
    };
    checkRatingStatus();
    return () => { isMounted = false; };
  }, [ride._id, status, currentUserId]);

  const getStatusStyle = (s) => {
    switch (s) {
      case 'accepted':
      case 'confirmed': return { bg: '#E8F8F5', text: '#2ECC71', label: 'Confirmed' };
      case 'completed': return { bg: '#EBF5FB', text: '#3498DB', label: 'Completed' };
      case 'rejected': return { bg: '#FDEDEC', text: '#E74C3C', label: 'Rejected' };
      case 'cancelled': return { bg: '#F3F4F6', text: '#9CA3AF', label: 'Cancelled' };
      default: return { bg: '#FEF9E7', text: '#D97706', label: 'Pending' };
    }
  };

  const style = getStatusStyle(status);

  const handleChat = (e) => {
    e.stopPropagation();
    if (driver._id) navigate(`/chat/${driver._id}`);
  };

  return (
    <div className="br-card" onClick={() => navigate(`/ride/${ride._id}`)}>
      
      {/* STATUS PILL */}
      <div className="br-status-pill" style={{ backgroundColor: style.bg, color: style.text }}>
        {style.label}
      </div>

      <div className="br-card-header">
        <div className="br-date-badge">
          <span className="day">{new Date(ride.date || Date.now()).getDate()}</span>
          <span className="month">{new Date(ride.date || Date.now()).toLocaleString('default', { month: 'short' }).toUpperCase()}</span>
        </div>
        
        <div className="br-route-info">
          <div className="br-route-locations">
            <span className="loc">{ride.from}</span>
            <span className="arrow">→</span>
            <span className="loc">{ride.to}</span>
          </div>
          <p className="time-text">⏰ {ride.time}</p>
        </div>
      </div>

      <div className="br-divider"></div>

      <div className="br-card-body">
        <div className="br-driver-info">
          <img src={avatar} alt="Driver" className="br-avatar" />
          <div className="br-driver-text">
            <span className="name">{driver.name || "Unknown"}</span>
            <div className="rating-star">
              ⭐ <span>{driver.rating ? Number(driver.rating).toFixed(1) : 'New'}</span>
            </div>
          </div>
        </div>
        <div className="br-price-tag">₹{ride.pricePerSeat || ride.price}</div>
      </div>

      {/* FOOTER ACTIONS (Cancel button removed) */}
      <div className="br-card-footer">
        {(status === 'accepted' || status === 'completed') && (
          <button className="br-btn-action chat" onClick={handleChat}>
            💬 Chat
          </button>
        )}

        {(status === 'completed' && !hasRated) && (
          <button className="br-btn-action rate" onClick={() => navigate(`/ride/${ride._id}#rate`)}>
            ⭐ Rate Driver
          </button>
        )}

        {(status === 'completed' && hasRated) && (
          <div className="br-rated-label">✅ You rated this ride</div>
        )}

        {(status === 'cancelled' || status === 'rejected') && (
          <div className="br-closed-label">Booking Closed</div>
        )}
        
        {/* If pending and no other buttons, show a placeholder to keep design balanced */}
        {status === 'pending' && (
          <div className="br-pending-label">Waiting for driver approval...</div>
        )}
      </div>
    </div>
  );
}